<script setup lang="ts">
import { dir } from '@@/bits/current_dir'
import { __ } from '@@/bits/intl'
import { isPostPinned } from '@@/bits/post_state'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import type { Post } from '@@/types'
import { usePostActionMenuTrigger } from '@@/vuecomposables/usePostActionMenuTrigger'
import { useElementBounding, useElementVisibility } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

const props = defineProps<{
  index: number
  post: Post
  isPublished: boolean
  backgroundLuminance: 'light' | 'dark'
  commentCount: number
  reactionCount?: number
  reactionType?: string
}>()

const emit = defineEmits<{
  (e: 'show-comments'): void
}>()

const moreButtonRef = ref<InstanceType<typeof OzPlainButton>>()
const { showPostActionMenuWithTriggerRect } = usePostActionMenuTrigger()
const { isReactable, isCommentable } = storeToRefs(useSurfaceSettingsStore())

function onMoreActionsButtonClick(): void {
  if (moreButtonRef.value?.$el == null) return
  showPostActionMenuWithTriggerRect(moreButtonRef.value.$el as HTMLElement, props.post)
}

// #region sticky row number styling
const surfaceStore = useSurfaceStore()
const { skinPrimaryVariantColorAsRGB, skinPrimaryTextColor, isSkinColorAvailable, isFrozen } = storeToRefs(surfaceStore)

const containerRef = ref<InstanceType<typeof OzBox>>()
const { left, right } = useElementBounding(containerRef)
const isVisible = useElementVisibility(containerRef)
const isSticky = ref(false)

watch([left, right, isVisible], ([newLeft, newRight, newIsVisible]) => {
  if ((newIsVisible && newLeft === 0 && dir() === 'ltr') || (newIsVisible && newRight === 0 && dir() === 'rtl')) {
    isSticky.value = true
  } else {
    isSticky.value = false
  }
})

const containerStyle = computed(() => {
  if (isSticky.value && isSkinColorAvailable.value) {
    return {
      backgroundColor: skinPrimaryVariantColorAsRGB.value,
      color: skinPrimaryTextColor.value,
    }
  }
  return undefined
})
// #endregion

// #region comment/reaction action button
const hasComments = computed(() => props.commentCount > 0)
const hasReactions = computed(() => props.reactionCount != null && props.reactionCount > 0)

const commentsReactionsCount = computed(() => {
  if (hasComments.value && hasReactions.value) {
    return props.commentCount + props.reactionCount!
  } else if (hasComments.value) {
    return props.commentCount
  } else if (hasReactions.value) {
    return props.reactionCount!
  }
  return 0
})

const displayedTotalCount = computed(() => {
  if (commentsReactionsCount.value > 99) return '99+'
  return commentsReactionsCount.value.toString()
})

const reactionIconName = computed(() => {
  switch (props.reactionType) {
    case 'like':
      return currentUserReacted.value ? 'heart_filled' : 'heart_outline'
    case 'vote':
      return 'thumb_up_outline'
    case 'star':
      return 'star_outline'
    case 'grade':
      return 'checkbox_person_outline'
    default:
      return ''
  }
})

const showCompositeIcon = computed(() => {
  return (hasComments.value && hasReactions.value) || (isReactable.value && isCommentable.value)
})
const showCommentIcon = computed(() => {
  return hasComments.value || isCommentable.value
})
const showReactionIcon = computed(() => {
  return hasReactions.value || isReactable.value
})

const accessibilityLabel = computed(() => {
  if (commentsReactionsCount.value > 0) {
    return __('View %{count} comments and reactions', { count: commentsReactionsCount.value })
  }
  return __('View comments and reactions')
})
// #endregion

// #region reaction action button, only when the reaction is "likes"
const onCommentsReactionsButtonClick = () => {
  const onlyShowingReactionsIcon = !showCompositeIcon.value && !showCommentIcon.value && showReactionIcon.value
  if (onlyShowingReactionsIcon && props.reactionType === 'like') {
    togglePostLike()
  } else {
    emit('show-comments')
  }
}

const { createReaction, deleteReaction } = useReactionsStore()
const { canIReact } = storeToRefs(useSurfacePermissionsStore())
const { setSnackbar } = useGlobalSnackbarStore()
const { accumulatedReactionsByWishId } = storeToRefs(useReactionsStore())

const canReact = computed(
  () =>
    canIReact.value && // user permissions
    isReactable.value, // padlet settings
)

const currentUserReacted = computed(() => {
  if (props.post.id == null) return false
  return accumulatedReactionsByWishId.value[props.post.id]?.userReaction != null
})

const currentUserLiked = computed(() => {
  return props.reactionType === 'like' && currentUserReacted.value
})

const togglePostLike = () => {
  if (!canReact.value) {
    showReactionOffNotice()
    return
  }

  if (props.post.id != null) {
    currentUserReacted.value
      ? deleteReaction({ postId: props.post.id })
      : createReaction({ postId: props.post.id, value: 1 })
  }
}

function showReactionOffNotice(): void {
  if (isFrozen.value) {
    setSnackbar({
      message: __('Reactions are disabled on frozen padlets.'),
      notificationType: SnackbarNotificationType.error,
    })
  } else if (!isReactable.value) {
    setSnackbar({
      message: __('Reactions are disabled. Changes are not allowed.'),
      notificationType: SnackbarNotificationType.error,
    })
  } else {
    // if !canIReact.value
    setSnackbar({
      message: __('You do not have permission to react on this padlet.'),
      notificationType: SnackbarNotificationType.error,
    })
  }
}
// #endregion
</script>

<template>
  <OzBox
    ref="containerRef"
    :radius="isSticky ? 0 : 8"
    :color="OzBoxColors.None"
    :class="[
      'box-border',
      'w-6',
      'pt-2 pb-1',
      'flex flex-col justify-between items-center gap-y-1',
      'backdrop-blur',
      !isSticky && {
        'bg-white-100/[0.32] text-dark-text-100': backgroundLuminance === 'light',
        'bg-black/[0.24] text-light-text-100': backgroundLuminance === 'dark',
      },
      'transition-all duration-200',
      isSticky && 'rounded-e-lg',
    ]"
    :style="containerStyle"
  >
    <span class="text-body-tiny font-semibold">{{ index + 1 }}</span>
    <div class="flex flex-col items-center justify-end gap-0.5">
      <OzPlainButton
        v-if="isPublished && (hasComments || hasReactions || isReactable || isCommentable)"
        :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
        :size-preset="OzPlainButtonSizePreset.Bare"
        :dark-mode="backgroundLuminance === 'dark'"
        :class="[
          'flex items-center justify-center',
          'focus-visible:ring-2',
          'rounded-md',
          backgroundLuminance === 'light' && 'focus-visible:text-grape-500 focus-visible:border-grape',
          backgroundLuminance === 'dark' && 'focus-visible:text-canary-500 focus-visible:border-canary',
        ]"
        :aria-label="accessibilityLabel"
        :title="accessibilityLabel"
        :data-testid="'postRowCommentsReactionsButton'"
        @click="onCommentsReactionsButtonClick"
      >
        <div class="relative group flex flex-col items-center gap-y-0.5">
          <OzIcon
            v-if="showCompositeIcon"
            name="comment_star"
            :size="12"
            :class="[
              backgroundLuminance === 'light'
                ? ['text-dark-text-300', 'group-hover:text-dark-text-200']
                : ['text-light-text-300', 'group-hover:text-light-text-200'],
              'shrink-0',
            ]"
          />
          <OzIcon
            v-else-if="showCommentIcon"
            name="comment_outline"
            :size="12"
            :class="[
              backgroundLuminance === 'light'
                ? ['text-dark-text-300', 'group-hover:text-dark-text-200']
                : ['text-light-text-300', 'group-hover:text-light-text-200'],
              'shrink-0',
            ]"
          />
          <OzIcon
            v-else-if="showReactionIcon"
            :name="reactionIconName"
            :size="12"
            :class="{
              'text-grape-500': currentUserLiked && backgroundLuminance === 'light',
              'text-canary-500': currentUserLiked && backgroundLuminance === 'dark',
              'text-dark-text-300 group-hover:text-dark-text-200': !currentUserLiked && backgroundLuminance === 'light',
              'text-light-text-300 group-hover:text-light-text-200':
                !currentUserLiked && backgroundLuminance === 'dark',
              'shrink-0': true,
            }"
          />

          <span
            v-if="commentsReactionsCount > 0"
            :class="[
              'inline-flex items-center justify-center',
              'font-semibold text-[8px] leading-[8px]',
              backgroundLuminance === 'light'
                ? ['text-dark-text-300', 'group-hover:text-dark-text-200']
                : ['text-light-text-300', 'group-hover:text-light-text-200'],
            ]"
            data-testid="postRowCommentsReactionsCount"
          >
            {{ displayedTotalCount }}
          </span>
        </div>
      </OzPlainButton>

      <span
        v-if="isPostPinned(post)"
        class="inline-flex h-4 items-center justify-center"
        data-testid="postRowPinButton"
      >
        <OzIcon
          name="pin_filled"
          :size="12"
          :class="[backgroundLuminance === 'light' ? 'text-dark-text-300' : 'text-light-text-300', 'shrink-0']"
        />
      </span>

      <OzPlainButton
        ref="moreButtonRef"
        :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
        :size-preset="OzPlainButtonSizePreset.Bare"
        :dark-mode="backgroundLuminance === 'dark'"
        :class="[
          'post-actions-button', // used by SurfacePanelPostActionMenu.vue for anchoring the post actions popover
          'flex items-center justify-center',
          'focus-visible:ring-2',
          'h-4',
          'rounded-md',
          backgroundLuminance === 'light' && 'focus-visible:text-grape-500 focus-visible:border-grape',
          backgroundLuminance === 'dark' && 'focus-visible:text-canary-500 focus-visible:border-canary',
        ]"
        :aria-label="__('More post actions')"
        :title="__('More post actions')"
        data-testid="surfacePostRowNumberMoreActionsButton"
        @click="onMoreActionsButtonClick"
      >
        <OzIcon name="dot_3_horizontal" :size="12" />
      </OzPlainButton>
    </div>
  </OzBox>
</template>
